import { useMemo, useCallback, useEffect } from 'react'
import { useAbility } from '@casl/react'

import { NOTIFICATION_TYPE_VIEW } from '../constants'
import { AbilityContext } from 'shared/abilities'
import { ENABLED_FEATURES } from 'shared/utils/featureFlags'
import { useLoading, useServerError, usePreviousNonNullish } from 'shared/hooks'
import { ghProjectAdapter, productMatchAdapter, storiesAdapter, tagsAdapter } from '../utils/notificationAdapters'
import { useTags } from '../../Tags/useTags'

import { useGetNotificationsQuery } from '../graphql/getNotifications.types'
import { TNotifications, TNotificationsResponse } from '../types'

export const useNotifications = (newNotification?: boolean) => {
  const ability = useAbility(AbilityContext)
  const { getTagByName } = useTags({ labelId: '', spaceId: '' })
  const { data: notificationsResponse, loading, error, refetch } = useGetNotificationsQuery()
  const prevNotificationsResponse = usePreviousNonNullish(notificationsResponse?.getNotifications)

  const groupedByNotificationType = useMemo(() =>
    (notificationsResponse?.getNotifications ?? prevNotificationsResponse ??  [])
      .reduce((acc: Record<string, Record<string, TNotifications>>, item: TNotificationsResponse) => {
        const isGlobalHubEnabled = ENABLED_FEATURES.GLOBALHUB && ability.can('access', 'GlobalHub')
        const type = item?.notification_type

        if (type === 'ProductMatchGcdm') return productMatchAdapter(acc, item)
        if (type === 'GH_GLOBAL_PROJECT' && isGlobalHubEnabled) return ghProjectAdapter(acc, item)
        if (type === 'GH_GLOBAL_STORY' && isGlobalHubEnabled) return storiesAdapter(acc, item)
        if (type === 'GH_SUCCESS_STORY' && isGlobalHubEnabled) return storiesAdapter(acc, item)
        if (type === 'UnreadArgument') return storiesAdapter(acc, item)
        if (getTagByName(type ?? '')) return tagsAdapter(acc, item)

        return acc
      }, {
        UnreadArgument: {},
        ProductMatchGcdm: {},
        GH_GLOBAL_STORY: {},
        GH_GLOBAL_PROJECT: {}
      }),[notificationsResponse?.getNotifications, prevNotificationsResponse, ability, getTagByName])

  const getNotificationsByType = useCallback((type: string) =>
    groupedByNotificationType[type] ? Object.values(groupedByNotificationType[type]) : [],[groupedByNotificationType])

  const getNotifications = useCallback((mode?: NOTIFICATION_TYPE_VIEW) => {
    const notificationTypes = Object.keys(groupedByNotificationType).filter((key) => {
      if (!mode) return true
      return mode === NOTIFICATION_TYPE_VIEW.GLOBALHUB ? !!key.includes('GH') : !key.includes('GH')
    })

    return notificationTypes
      .reduce((acc: TNotifications[], n) => [...acc, ...getNotificationsByType(n)], [])
      .sort((a, b) => b.last_updated - a.last_updated)
  }, [groupedByNotificationType, getNotificationsByType])

  useEffect(() => {
    refetch()
  }, [newNotification, refetch])

  useLoading({ isLoading: loading })
  useServerError([error])

  return {
    getNotificationsByType,
    getNotifications,
    refetch,
    loading,
    error
  }
}
